import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { Select } from 'antd';
import {
  createFocusHandle,
  createOnBlur,
  FilterInputProps,
  FocusRef,
  NoContentItem,
  SampleScopeUsersType,
  useAutoClearColumnFilterIfNotInOptions,
  useSampleScopeUsersQuery,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './SampleUserSelectFilterInput.translations.json';
import { useSampleContext } from './state/SampleContext';

const { addTranslations, useTranslation } = i18nNsFactory(
  'sample-user-select-filter-input',
);
addTranslations(translations);

const filterUsernames = (
  data: SampleScopeUsersType | undefined,
  columnId: string,
) => {
  if (columnId === 'dataManagerUsername') {
    return data?.dataManagers;
  }
  return data?.dataModifiedBy;
};

export const SampleUserSelectFilterInput = forwardRef<
  FocusRef,
  FilterInputProps
>((props, ref) => {
  const { header, onBlur } = props;
  const { t } = useTranslation();
  const id = `${header.column.id}-filter-input`;
  const { groupOrDescriptionId, date, sampleType } = useSampleContext();

  useImperativeHandle(ref, () => createFocusHandle(id));
  const handleOnBlur = useMemo(
    () => createOnBlur<HTMLInputElement>(id, onBlur),
    [id, onBlur],
  );

  const { data, isFetching } = useSampleScopeUsersQuery({
    id: groupOrDescriptionId,
    valueDate: date,
    type: sampleType,
  });

  const options = useMemo(() => {
    const usernames = filterUsernames(data, header.column.id);
    return (usernames ?? []).map((username) => ({
      label: username,
      value: username,
    }));
  }, [data, header.column.id]);

  useAutoClearColumnFilterIfNotInOptions({
    header,
    options,
    isEnabled: !isFetching,
  });

  const handleChange = (value?: string) => {
    header.column.setFilterValue(value ?? null);
  };

  const value = header.column.getFilterValue() as string;

  return (
    <Select
      id={id}
      className={id}
      placeholder={t('placeholder')}
      options={options}
      value={value === null ? undefined : value}
      onChange={handleChange}
      onBlur={handleOnBlur}
      styles={{
        popup: { root: { width: 'max-content' } },
      }}
      allowClear
      style={{ minWidth: 'max-content' }}
      defaultOpen
      notFoundContent={<NoContentItem />}
    />
  );
});
